import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Trash2, Pencil, Plus, Mail, Check, ImageIcon, BookOpen } from "lucide-react";

const EMPTY_FORM = {
  title: "",
  year: "",
  medium: "",
  dimensions: "",
  description: "",
  artist_statement: "",
  tags: "",
  price: "",
  availability: "available",
  image_data: "",
};

function ArtworkForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState({ ...EMPTY_FORM, ...(initial || {}) });
  const [saving, setSaving] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      toast.error("Image must be under 50MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => update("image_data", reader.result);
    reader.onerror = () => toast.error("Could not read the file. Try another image.");
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!form.title || !form.image_data) {
      toast.error("Title and image are required.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        year: form.year ? Number(form.year) : null,
        tags: typeof form.tags === "string"
          ? form.tags.split(",").map((t) => t.trim()).filter(Boolean)
          : form.tags || [],
      };
      await onSave(payload);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid gap-5 max-h-[70vh] overflow-y-auto pr-2" data-testid="artwork-form">
      <div>
        <Label className="plaque-label">Title *</Label>
        <Input
          value={form.title}
          onChange={(e) => update("title", e.target.value)}
          data-testid="form-title-input"
          className="bg-[#0F0F0F] border-white/10 mt-2"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="plaque-label">Year</Label>
          <Input
            type="number"
            value={form.year || ""}
            onChange={(e) => update("year", e.target.value)}
            data-testid="form-year-input"
            className="bg-[#0F0F0F] border-white/10 mt-2"
          />
        </div>
        <div>
          <Label className="plaque-label">Dimensions</Label>
          <Input
            value={form.dimensions}
            onChange={(e) => update("dimensions", e.target.value)}
            placeholder="36 × 48 in"
            data-testid="form-dimensions-input"
            className="bg-[#0F0F0F] border-white/10 mt-2"
          />
        </div>
      </div>
      <div>
        <Label className="plaque-label">Medium</Label>
        <Input
          value={form.medium}
          onChange={(e) => update("medium", e.target.value)}
          placeholder="Oil on linen"
          data-testid="form-medium-input"
          className="bg-[#0F0F0F] border-white/10 mt-2"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="plaque-label">Price</Label>
          <Input
            value={form.price}
            onChange={(e) => update("price", e.target.value)}
            placeholder="$2,400 or POA"
            data-testid="form-price-input"
            className="bg-[#0F0F0F] border-white/10 mt-2"
          />
        </div>
        <div>
          <Label className="plaque-label">Availability</Label>
          <Select value={form.availability} onValueChange={(v) => update("availability", v)}>
            <SelectTrigger className="bg-[#0F0F0F] border-white/10 mt-2" data-testid="form-availability-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="available">Available</SelectItem>
              <SelectItem value="sold">Sold</SelectItem>
              <SelectItem value="reserved">Reserved</SelectItem>
              <SelectItem value="nfs">Not for sale</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label className="plaque-label">Tags (comma-separated)</Label>
        <Input
          value={Array.isArray(form.tags) ? form.tags.join(", ") : form.tags}
          onChange={(e) => update("tags", e.target.value)}
          placeholder="abstract, dark, blue"
          data-testid="form-tags-input"
          className="bg-[#0F0F0F] border-white/10 mt-2"
        />
      </div>
      <div>
        <Label className="plaque-label">Description</Label>
        <Textarea
          rows={3}
          value={form.description}
          onChange={(e) => update("description", e.target.value)}
          data-testid="form-description-input"
          className="bg-[#0F0F0F] border-white/10 mt-2"
        />
      </div>
      <div>
        <Label className="plaque-label">Artist Statement</Label>
        <Textarea
          rows={3}
          value={form.artist_statement}
          onChange={(e) => update("artist_statement", e.target.value)}
          data-testid="form-statement-input"
          className="bg-[#0F0F0F] border-white/10 mt-2"
        />
      </div>
      <div>
        <Label className="plaque-label">Image *</Label>
        <div className="mt-2 flex items-center gap-4">
          {form.image_data ? (
            <img
              src={form.image_data}
              alt="preview"
              className="w-24 h-24 object-cover border border-white/10"
              data-testid="form-image-preview"
            />
          ) : (
            <div className="w-24 h-24 bg-[#0F0F0F] border border-white/10 flex items-center justify-center text-zinc-600">
              <ImageIcon className="w-6 h-6" />
            </div>
          )}
          <label className="cursor-pointer text-xs tracking-[0.3em] uppercase border border-white/15 hover:border-[#D4AF37] px-4 py-3 text-zinc-300">
            {form.image_data ? "Replace image" : "Upload image"}
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleImage}
              data-testid="form-image-input"
            />
          </label>
        </div>
      </div>
      <div className="flex justify-end gap-3 pt-2 border-t border-white/5">
        <Button variant="outline" onClick={onCancel} data-testid="form-cancel-button">
          Cancel
        </Button>
        <Button
          onClick={save}
          disabled={saving}
          className="bg-[#D4AF37] text-black hover:bg-[#E5C158]"
          data-testid="form-save-button"
        >
          {saving ? "Saving…" : "Save"}
        </Button>
      </div>
    </div>
  );
}

function ArtworksTab() {
  const [artworks, setArtworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => {
    setLoading(true);
    api
      .get("/artworks")
      .then((r) => setArtworks(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const create = async (payload) => {
    try {
      await api.post("/artworks", payload);
      toast.success("Artwork hung on the wall.");
      setOpenCreate(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to save");
    }
  };

  const update = async (id, payload) => {
    try {
      await api.patch(`/artworks/${id}`, payload);
      toast.success("Artwork updated.");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to update");
    }
  };

  const remove = async (id) => {
    if (!confirm("Remove this artwork from the gallery?")) return;
    try {
      await api.delete(`/artworks/${id}`);
      toast.success("Removed.");
      load();
    } catch (e) {
      toast.error("Failed to remove");
    }
  };

  return (
    <div className="space-y-6" data-testid="artworks-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-serif-display text-3xl font-light">Works on view</h2>
          <p className="plaque-label mt-1">{artworks.length} in the collection</p>
        </div>
        <Dialog open={openCreate} onOpenChange={setOpenCreate}>
          <DialogTrigger asChild>
            <Button className="bg-[#D4AF37] text-black hover:bg-[#E5C158]" data-testid="new-artwork-button">
              <Plus className="w-4 h-4 mr-2" /> New artwork
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-serif-display text-3xl font-light">Hang new artwork</DialogTitle>
            </DialogHeader>
            <ArtworkForm onSave={create} onCancel={() => setOpenCreate(false)} />
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <p className="text-zinc-500">Loading…</p>
      ) : artworks.length === 0 ? (
        <div className="border border-dashed border-white/10 p-16 text-center text-zinc-500" data-testid="artworks-empty">
          The walls are bare. Hang your first piece.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {artworks.map((a) => (
            <div
              key={a.id}
              className="border border-white/10 bg-[#0F0F0F] p-4 group"
              data-testid={`admin-artwork-${a.id}`}
            >
              <div className="aspect-[4/5] bg-[#1a1a1a] overflow-hidden mb-3">
                <img src={a.thumb_data || a.image_data} alt={a.title} className="w-full h-full object-contain" />
              </div>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-serif-display text-xl truncate">{a.title}</p>
                  <p className="plaque-label mt-1">{a.year || "—"} · {a.availability}</p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={async () => {
                      // List endpoint strips image_data for speed; fetch the
                      // full record before opening the edit dialog so the
                      // preview + required-image check still work.
                      try {
                        const r = await api.get(`/artworks/${a.id}`);
                        setEditing(r.data);
                      } catch {
                        setEditing(a);
                      }
                    }}
                    className="p-2 border border-white/10 hover:border-[#D4AF37] text-zinc-400 hover:text-[#D4AF37]"
                    data-testid={`edit-artwork-${a.id}`}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => remove(a.id)}
                    className="p-2 border border-white/10 hover:border-red-500 text-zinc-400 hover:text-red-400"
                    data-testid={`delete-artwork-${a.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-3xl font-light">Edit artwork</DialogTitle>
          </DialogHeader>
          {editing && (
            <ArtworkForm
              initial={editing}
              onSave={(p) => update(editing.id, p)}
              onCancel={() => setEditing(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InquiriesTab() {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api
      .get("/inquiries")
      .then((r) => setInquiries(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const markRead = async (id) => {
    try {
      await api.patch(`/inquiries/${id}/read`);
      load();
    } catch (e) {
      toast.error("Failed to update");
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this inquiry?")) return;
    try {
      await api.delete(`/inquiries/${id}`);
      load();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const unread = inquiries.filter((i) => !i.read).length;

  return (
    <div className="space-y-6" data-testid="inquiries-tab">
      <div>
        <h2 className="font-serif-display text-3xl font-light">Correspondence</h2>
        <p className="plaque-label mt-1">
          {unread} unread · {inquiries.length} total
        </p>
      </div>
      {loading ? (
        <p className="text-zinc-500">Loading…</p>
      ) : inquiries.length === 0 ? (
        <div className="border border-dashed border-white/10 p-16 text-center text-zinc-500" data-testid="inquiries-empty">
          No messages yet.
        </div>
      ) : (
        <div className="space-y-3">
          {inquiries.map((i) => (
            <div
              key={i.id}
              className={`border bg-[#0F0F0F] p-5 ${
                i.read ? "border-white/5" : "border-[#D4AF37]/30"
              }`}
              data-testid={`inquiry-${i.id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <p className="font-serif-display text-xl">{i.name}</p>
                    <span className="text-xs text-zinc-500">{i.email}</span>
                    {!i.read && (
                      <span className="text-[10px] tracking-widest uppercase text-[#D4AF37] border border-[#D4AF37]/40 px-2 py-0.5">
                        new
                      </span>
                    )}
                  </div>
                  {i.artwork_title && (
                    <p className="plaque-label mt-2">re: {i.artwork_title}</p>
                  )}
                  <p className="text-zinc-300 mt-3 leading-relaxed text-sm whitespace-pre-wrap">{i.message}</p>
                  <p className="text-xs text-zinc-600 mt-3">
                    {new Date(i.created_at).toLocaleString()}
                  </p>
                </div>
                <div className="flex gap-2 shrink-0">
                  {!i.read && (
                    <button
                      onClick={() => markRead(i.id)}
                      className="p-2 border border-white/10 hover:border-[#D4AF37] text-zinc-400 hover:text-[#D4AF37]"
                      data-testid={`mark-read-${i.id}`}
                      title="Mark as read"
                    >
                      <Check className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <a
                    href={`mailto:${i.email}?subject=Re: your inquiry about ${encodeURIComponent(
                      i.artwork_title || "the gallery"
                    )}`}
                    className="p-2 border border-white/10 hover:border-[#D4AF37] text-zinc-400 hover:text-[#D4AF37]"
                    data-testid={`reply-${i.id}`}
                    title="Reply"
                  >
                    <Mail className="w-3.5 h-3.5" />
                  </a>
                  <button
                    onClick={() => remove(i.id)}
                    className="p-2 border border-white/10 hover:border-red-500 text-zinc-400 hover:text-red-400"
                    data-testid={`delete-inquiry-${i.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  return (
    <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-12" data-testid="admin-dashboard">
      <p className="plaque-label">Curator's Desk</p>
      <h1 className="font-serif-display font-light text-5xl mt-2">Manage the Gallery</h1>

      <Tabs defaultValue="artworks" className="mt-12">
        <TabsList className="bg-[#0F0F0F] border border-white/10 rounded-sm">
          <TabsTrigger value="artworks" data-testid="tab-artworks">
            Artworks
          </TabsTrigger>
          <TabsTrigger value="inquiries" data-testid="tab-inquiries">
            Inquiries
          </TabsTrigger>
          <TabsTrigger value="guestbook" data-testid="tab-guestbook">
            Guestbook
          </TabsTrigger>
        </TabsList>
        <TabsContent value="artworks" className="mt-8">
          <ArtworksTab />
        </TabsContent>
        <TabsContent value="inquiries" className="mt-8">
          <InquiriesTab />
        </TabsContent>
        <TabsContent value="guestbook" className="mt-8">
          <GuestbookTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function GuestbookTab() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api
      .get("/guestbook/admin")
      .then((r) => setEntries(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const remove = async (id) => {
    if (!confirm("Delete this guestbook entry?")) return;
    try {
      await api.delete(`/guestbook/${id}`);
      toast.success("Entry removed.");
      load();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="space-y-6" data-testid="guestbook-admin-tab">
      <div>
        <h2 className="font-serif-display text-3xl font-light flex items-center gap-3">
          <BookOpen className="w-6 h-6 text-[#D4AF37]" />
          Guestbook
        </h2>
        <p className="plaque-label mt-1">{entries.length} signatures</p>
      </div>

      {loading ? (
        <p className="text-zinc-500">Loading…</p>
      ) : entries.length === 0 ? (
        <div className="border border-dashed border-white/10 p-16 text-center text-zinc-500" data-testid="guestbook-admin-empty">
          The book is empty.
        </div>
      ) : (
        <div className="space-y-3">
          {entries.map((g) => (
            <div
              key={g.id}
              className="border border-white/5 bg-[#0F0F0F] p-5"
              data-testid={`admin-guestbook-${g.id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <p className="text-zinc-300 italic font-serif-display text-lg leading-relaxed">
                    "{g.message}"
                  </p>
                  <div className="mt-3 flex flex-wrap items-baseline gap-x-3 text-xs">
                    <span className="text-zinc-200">— {g.name}</span>
                    {g.location && <span className="text-zinc-500 italic">{g.location}</span>}
                    <span className="text-zinc-600">
                      {new Date(g.created_at).toLocaleString()}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => remove(g.id)}
                  className="p-2 border border-white/10 hover:border-red-500 text-zinc-400 hover:text-red-400 shrink-0"
                  data-testid={`delete-guestbook-${g.id}`}
                  title="Delete"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
